// n8n API Service - Handles all communication with n8n workflows
// UPDATED: Jan 2026 - Added updateProspectNotes and archiveProspect methods
// UPDATED: Jan 2026 - Added retry logic with exponential backoff

const N8N_BASE_URL = import.meta.env.VITE_N8N_BASE_URL || 'https://freeds.app.n8n.cloud'
const N8N_API_KEY = import.meta.env.VITE_N8N_API_KEY

// Retry configuration
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
}

// Errors that should trigger a retry (transient errors)
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504]

/**
 * Sleep for a given number of milliseconds
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Calculate delay with exponential backoff and jitter
 */
const getRetryDelay = (attempt, baseDelayMs, maxDelayMs) => {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt)
  const jitter = Math.random() * 0.3 * exponentialDelay // Add up to 30% jitter
  return Math.min(exponentialDelay + jitter, maxDelayMs)
}

class N8NApiService {
  /**
   * Generic webhook caller with error handling and retry logic
   * @param {string} endpoint - The webhook endpoint
   * @param {Object} payload - The request payload
   * @param {Object} options - Optional configuration
   * @param {boolean} options.retry - Whether to retry on failure (default: true)
   * @param {number} options.maxRetries - Maximum number of retries
   */
  async callWebhook(endpoint, payload, options = {}) {
    const { retry = true, maxRetries = DEFAULT_RETRY_CONFIG.maxRetries } = options

    let lastError = null

    for (let attempt = 0; attempt <= (retry ? maxRetries : 0); attempt++) {
      try {
        const headers = {
          'Content-Type': 'application/json',
        }

        // Add API key if configured (for security)
        if (N8N_API_KEY) {
          headers['x-api-key'] = N8N_API_KEY
        }

        const response = await fetch(`${N8N_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`)
          error.status = response.status
          error.endpoint = endpoint

          // Only retry on transient errors
          if (retry && RETRYABLE_STATUS_CODES.includes(response.status) && attempt < maxRetries) {
            const delay = getRetryDelay(attempt, DEFAULT_RETRY_CONFIG.baseDelayMs, DEFAULT_RETRY_CONFIG.maxDelayMs)
            console.warn(`n8n API call to ${endpoint} failed with status ${response.status}, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries})`)
            await sleep(delay)
            lastError = error
            continue
          }

          throw error
        }

        return await response.json()
      } catch (error) {
        lastError = error
        error.endpoint = endpoint

        // Retry on network errors (no status code means fetch failed)
        if (retry && !error.status && attempt < maxRetries) {
          const delay = getRetryDelay(attempt, DEFAULT_RETRY_CONFIG.baseDelayMs, DEFAULT_RETRY_CONFIG.maxDelayMs)
          console.warn(`n8n API call to ${endpoint} failed with network error, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${maxRetries})`)
          await sleep(delay)
          continue
        }

        // Don't retry on non-retryable errors or if retries exhausted
        if (attempt === maxRetries || !retry || (error.status && !RETRYABLE_STATUS_CODES.includes(error.status))) {
          console.error(`n8n API call failed for ${endpoint}:`, error)
          throw error
        }
      }
    }

    // Should not reach here, but just in case
    throw lastError
  }

  /**
   * Get user status from Airtable via n8n
   * @param {string} firebaseUid - Firebase user ID (the prospect being viewed)
   * @param {Object} options - Optional parameters
   * @param {string} options.acting - Admin's Firebase UID if impersonating (for audit)
   * @returns {Promise<{found: boolean, prospect?: Object}>}
   */
  async getUserStatus(firebaseUid, options = {}) {
    const payload = {
      firebase_uid: firebaseUid,
    }
    
    // If an admin is acting on behalf of someone (impersonation)
    if (options.acting) {
      payload.acting = options.acting
    }
    
    return this.callWebhook('/webhook/get-user-status', payload)
  }

  /**
   * Create new user record in Airtable via n8n (LEGACY - minimal data)
   * @deprecated Use createUserWithDetails instead for new signups
   * @param {string} firebaseUid - Firebase user ID
   * @param {string} email - User email
   * @param {string} name - User display name
   * @returns {Promise<{success: boolean, prospect_id: string}>}
   */
  async createUser(firebaseUid, email, name = '') {
    return this.callWebhook('/webhook/create-new-user', {
      firebase_uid: firebaseUid,
      email: email,
      name: name,
    })
  }

  /**
   * Create new user with full contact details (combined signup)
   * All users start at stage 02_INTEREST_CONFIRMED (intro meeting booking)
   * @param {string} firebaseUid - Firebase user ID
   * @param {Object} data - User details
   * @returns {Promise<{success: boolean, prospect_id: string, initial_stage: string}>}
   */
  async createUserWithDetails(firebaseUid, data) {
    return this.callWebhook('/webhook/create-new-user', {
      firebase_uid: firebaseUid,
      email: data.email,
      full_name: data.full_name,
      company_name: data.company_name,
      phone: data.phone || ''
      // Note: demo_preference removed - all users start with intro meeting
    })
  }

  /**
   * Confirm user interest (when they click Continue on USP page)
   * @deprecated No longer needed - signup now includes demo preference
   * Transitions user from stage 01 to stage 02
   * @param {string} firebaseUid - Firebase user ID
   * @returns {Promise<{success: boolean}>}
   */
  async confirmInterest(firebaseUid) {
    return this.callWebhook('/webhook/interest-confirmed', {
      firebase_uid: firebaseUid,
    })
  }

  /**
   * Submit demo selection with contact details
   * @deprecated No longer needed for new signups - use createUserWithDetails instead
   * Still used by GenericDemoPage to switch from generic → personalised
   * Transitions user from stage 02 to stage 03 (generic) or 04 (personalised)
   * @param {string} firebaseUid - Firebase user ID
   * @param {Object} data - Contact details and demo preference
   * @param {Object} options - Optional parameters
   * @param {string} options.acting - Admin's Firebase UID if impersonating (for audit)
   * @returns {Promise<{success: boolean, next_stage: string}>}
   */
  async submitDemoSelection(firebaseUid, data, options = {}) {
    const payload = {
      firebase_uid: firebaseUid,
      ...data,
    }
    
    if (options.acting) {
      payload.acting = options.acting
    }
    
    return this.callWebhook('/webhook/demo-selected', payload)
  }

  /**
   * Initiate consent/DocuSign process
   * @param {string} firebaseUid - Firebase user ID
   * @param {Object} data - Consent data
   * @param {Object} options - Optional parameters
   * @param {string} options.acting - Admin's Firebase UID if impersonating (for audit)
   * @returns {Promise<{success: boolean, envelope_id?: string}>}
   */
  async initiateConsent(firebaseUid, data, options = {}) {
    const payload = {
      firebase_uid: firebaseUid,
      ...data,
    }
    
    if (options.acting) {
      payload.acting = options.acting
    }
    
    return this.callWebhook('/webhook/consent-request', payload)
  }

  /**
   * Submit and validate dashcam provider API token
   * UPDATED Dec 2025: Organization ID no longer required
   * @param {string} firebaseUid - Firebase user ID
   * @param {string} apiToken - Dashcam provider API token
   * @param {string} region - Provider region (us/eu/ca)
   * @param {Object} options - Optional parameters
   * @param {string} options.acting - Admin's Firebase UID if impersonating (for audit)
   * @returns {Promise<{success: boolean, error?: string, error_type?: string}>}
   */
  async validateSamsaraToken(firebaseUid, apiToken, region, options = {}) {
    const payload = {
      firebase_uid: firebaseUid,
      api_token: apiToken,
      region: region,
    }
    
    if (options.acting) {
      payload.acting = options.acting
    }
    
    return this.callWebhook('/webhook/token-submit', payload)
  }

  /**
   * Track demo viewed
   * @param {string} firebaseUid - Firebase user ID
   * @returns {Promise<{success: boolean}>}
   */
  async trackDemoViewed(firebaseUid) {
    return this.callWebhook('/webhook/demo-viewed', {
      firebase_uid: firebaseUid,
    })
  }

  // ==========================================
  // ADMIN METHODS
  // ==========================================

  /**
   * Check if user is an admin
   * @param {string} firebaseUid - Firebase user ID
   * @returns {Promise<{is_admin: boolean, role?: string, name?: string}>}
   */
  async checkAdminStatus(firebaseUid) {
    return this.callWebhook('/webhook/check-admin-status', {
      firebase_uid: firebaseUid,
    })
  }

  /**
   * Get all prospects (admin only)
   * @param {string} firebaseUid - Firebase user ID of admin
   * @returns {Promise<{success: boolean, count: number, prospects: Array}>}
   */
  async getAllProspects(firebaseUid) {
    return this.callWebhook('/webhook/get-all-prospects', {
      firebase_uid: firebaseUid,
    })
  }

  /**
   * Initiate personalised demo pathway for a prospect (admin only)
   * Moves prospect from intro stage (02/03) to consent stage (03B)
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {string} adminUid - Firebase user ID of the admin performing the action
   * @returns {Promise<{success: boolean, previous_stage: string, new_stage: string}>}
   */
  async initiatePersonalisedDemo(prospectUid, adminUid) {
    return this.callWebhook('/webhook/admin-initiate-personalised', {
      firebase_uid: prospectUid,
      acting: adminUid,
    })
  }

  /**
   * Resend the DocuSign consent envelope for a prospect (admin only)
   * Voids the existing envelope and creates a fresh one using the signer
   * details already on file. Used when the prospect's envelope has expired
   * or they've lost the original signing email.
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {string} adminUid - Firebase user ID of the admin performing the action
   * @returns {Promise<{success: boolean, stage?: string, envelope_id?: string, error?: string, error_type?: string}>}
   */
  async resendConsentEnvelope(prospectUid, adminUid) {
    return this.callWebhook('/webhook/consent-resend', {
      firebase_uid: prospectUid,
      acting: adminUid,
    })
  }

  /**
   * Update notes for a prospect (admin only)
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {string} notes - New notes content (freeform text)
   * @param {string} adminUid - Firebase user ID of the admin performing the action
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async updateProspectNotes(prospectUid, notes, adminUid) {
    return this.callWebhook('/webhook/update-prospect-notes', {
      firebase_uid: prospectUid,
      notes: notes,
      acting: adminUid,
    })
  }

  /**
   * Archive or unarchive a prospect (admin only)
   * Archived prospects are hidden from default admin view and see "account inactive" message on login
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {boolean} archived - true to archive, false to unarchive
   * @param {string} adminUid - Firebase user ID of the admin performing the action
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async archiveProspect(prospectUid, archived, adminUid) {
    return this.callWebhook('/webhook/archive-prospect', {
      firebase_uid: prospectUid,
      archived: archived,
      acting: adminUid,
    })
  }
  /**
   * Provision a prospect as a FleetVision user (admin only)
   * Creates user in FleetVision system and sends welcome email
   * @param {string} prospectUid - Firebase user ID of the prospect to provision
   * @param {string} adminUid - Firebase user ID of the admin performing the action
   * @returns {Promise<{success: boolean, message?: string, error?: string, error_type?: string}>}
   */
  async provisionUser(prospectUid, adminUid) {
    return this.callWebhook('/webhook/admin-provision-user', {
      firebase_uid: prospectUid,
      acting: adminUid,
    })
  }

  // ==========================================
  // ORGANIZATION USERS METHODS (Jan 2026)
  // ==========================================

  /**
   * Get organization users for a prospect (admin only)
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {string} adminUid - Firebase user ID of the admin
   * @returns {Promise<{success: boolean, users: Array<{id: string, email: string, full_name: string, role: string, provisioning_status: string, error_message?: string, provisioned_at?: string}>}>}
   */
  async getOrganizationUsers(prospectUid, adminUid) {
    return this.callWebhook('/webhook/get-organization-users', {
      firebase_uid: prospectUid,
      acting: adminUid,
    })
  }

  /**
   * Add users to an organization (admin only)
   * Creates pending records in Organization_Users table
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {Array<{email: string, full_name: string, role?: string}>} users - Users to add
   * @param {string} adminUid - Firebase user ID of the admin
   * @returns {Promise<{success: boolean, added: Array<{email: string, user_id: string}>, skipped: Array<{email: string, reason: string}>, errors: Array<{email: string, error: string}>}>}
   */
  async addOrganizationUsers(prospectUid, users, adminUid) {
    return this.callWebhook('/webhook/add-organization-users', {
      firebase_uid: prospectUid,
      users: users,
      acting: adminUid,
    })
  }

  /**
   * Provision pending organization users (admin only)
   * Creates Firebase accounts and FleetVision users for pending users
   * @param {string} prospectUid - Firebase user ID of the prospect
   * @param {Array<string>|null} userIds - Specific user IDs to provision, or null for all pending
   * @param {string} adminUid - Firebase user ID of the admin
   * @returns {Promise<{success: boolean, total: number, provisioned: number, failed: number, results: Array<{email: string, status: string, error?: string}>}>}
   */
  async provisionOrganizationUsers(prospectUid, userIds, adminUid) {
    return this.callWebhook('/webhook/provision-organization-users', {
      firebase_uid: prospectUid,
      user_ids: userIds,
      acting: adminUid,
    })
  }

  /**
   * Remove a pending organization user (admin only)
   * Only pending users can be removed
   * @param {string} userId - Airtable record ID of the organization user
   * @param {string} adminUid - Firebase user ID of the admin
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async removeOrganizationUser(userId, adminUid) {
    return this.callWebhook('/webhook/remove-organization-user', {
      user_id: userId,
      acting: adminUid,
    })
  }
}

export const n8nApi = new N8NApiService()
